import { fetchJson } from "./apiClient";

export type CreateLeadPayload = {
  cardOwnerId: string;
  cardOwnerEmail: string;
  cardOwnerName?: string;
  fullName: string;
  email: string;
  phoneNumber?: string;
  message?: string; // message will be emailed to owner; not persisted
  source?: string; // e.g., 'email_form'
};

export async function createLead(payload: CreateLeadPayload) {
  return fetchJson("/api/leads", {
    method: "POST",
    body: JSON.stringify({ source: "email_form", ...payload }),
  });
}

export async function listLeadsByOwner(ownerId: string) {
  const q = encodeURIComponent(ownerId);
  // Client-side guard: avoid unauthenticated network calls that 401
  try {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('dev_admin_token') || localStorage.getItem('owner_token') || '';
      if (!token) {
        // Briefly wait for auth token to appear (post-login race), then re-check
        await new Promise<void>((resolve) => setTimeout(resolve, 250));
        const retryToken = localStorage.getItem('dev_admin_token') || localStorage.getItem('owner_token') || '';
        if (!retryToken) {
          const err: any = new Error('Not authenticated');
          err.status = 401;
          throw err;
        }
      }
      // Validate session server-side: if token is stale/invalid, avoid calling /api/leads
      try {
        const me: any = await fetchJson('/api/me');
        // When no Bearer token is present, /api/me returns { authenticated: false }
        if (!me || me.authenticated === false) {
          const err: any = new Error('Not authenticated');
          err.status = 401;
          throw err;
        }
        // If owner mode, ensure the requested ownerId matches the current session when not admin
        const isAdmin = !!(me && typeof me.adminRole === 'string' && me.adminRole);
        if (!isAdmin) {
          const sessId = String(me.id || '');
          if (sessId && sessId !== ownerId) {
            const err: any = new Error('Forbidden');
            err.status = 403;
            throw err;
          }
        }
      } catch (e: any) {
        // If the validation fails with 401/403, clear stale tokens and stop here to avoid 401 spam
        const s = Number(e?.status || 0);
        if (s === 401 || s === 403) {
          try { localStorage.removeItem('owner_token'); } catch {}
          try { localStorage.removeItem('dev_admin_token'); } catch {}
          const err: any = new Error('Not authenticated');
          err.status = 401;
          throw err;
        }
        // Non-auth validation errors: proceed to let caller handle
      }
    }
  } catch {}
  return fetchJson(`/api/leads?ownerId=${q}`);
}

export async function listAllLeads() {
  return fetchJson(`/api/admin/leads`);
}

export type CohortKind = 'leads_existing' | 'leads_existing_users' | 'leads_non' | 'leads_non_users' | 'leads_incomplete_signup' | 'users_subscribed' | 'users_unsubscribed' | 'users_expired';

export async function fetchCohort(kind: CohortKind) {
  const q = encodeURIComponent(kind);
  return fetchJson(`/api/admin/leads/cohort?kind=${q}`);
}

export async function markLeadAsRead(id: string) {
  return fetchJson(`/api/leads/${encodeURIComponent(id)}/read`, { method: "PATCH" });
}

export async function deleteLead(id: string) {
  return fetchJson(`/api/leads/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export type IncompleteSignupLead = {
  fullName: string;
  email: string;
  phoneNumber?: string;
  note?: string; // optional context like selected plan or step
  position?: string;
  country?: string;
  company?: string;
  sessionId?: string;
  reservedUsername?: string;
};

export async function recordIncompleteSignupLead(payload: IncompleteSignupLead) {
  return fetchJson('/api/leads/incomplete', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
